import { useEffect, useMemo, useState } from "react";
import { DocumentDTO } from "../types/DocumentDTO";
import {
  getDocumentMPK,
  getMPKDictionaries,
  type DimensionValue,
  type MPKDictionariesResponse,
  type MPKDimensionDefinition,
  type MPKRowDTO,
} from "../api/mpk";

export type MpkDimensionSelection = {
  dimensionCode: string;
  dimensionValueCode?: string;
};

export type MpkRow = {
  id: string;
  systemId?: string;

  amount: number;
  percent: number;
  purchaseCost?: string;
  purchaseCostText?: string;

  /**
   * Nowy model:
   * key = "dimension1"..."dimension10"
   * value = kod wartości wymiaru, np. "ADM", "P0001"
   */
  dimensionValues: Record<string, string | undefined>;

  /**
   * Nowy model payloadu do zapisu.
   */
  dimensions: MpkDimensionSelection[];

  /**
   * Tymczasowa kompatybilność ze starym komponentem DocumentMpkTable.
   * Usuniemy po pełnym przejściu na DocumentMpkV2.
   */
  purchaserCode?: string;
  purchaserName?: string;
  customerGroupCode?: string;
  customerGroupName?: string;
};

function round2(value: number) {
  return Number(value.toFixed(2));
}

function isAlmostEqual(a: number, b: number, precision = 0.01) {
  return Math.abs(a - b) <= precision;
}

function makeLocalId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
}

function getRowField(
  row: MPKRowDTO,
  prefix: "dimensionCode" | "dimensionValueCode",
  order: number
): string | undefined {
  return row[`${prefix}${order}` as keyof MPKRowDTO] as string | undefined;
}

function findDimensionValueName(
  values: DimensionValue[] | undefined,
  code?: string
) {
  if (!code) return undefined;

  return values?.find((value) => value.code === code)?.name;
}

function normalizeExistingRow(
  row: MPKRowDTO,
  activeDimensions: MPKDimensionDefinition[],
  documentNetAmount: number
): MpkRow {
  const amount = Number(row.amount || 0);

  const percent =
    documentNetAmount > 0
      ? round2((amount / documentNetAmount) * 100)
      : 0;

  const dimensionValues: Record<string, string | undefined> = {};

  /**
   * Czytamy wszystkie pola dimensionCode1...10 z BC.
   * Dzięki temu nie jesteśmy uzależnieni wyłącznie od pozycji,
   * tylko realnie mapujemy po dimensionCode.
   */
  for (let index = 1; index <= 10; index += 1) {
    const dimensionCode = getRowField(row, "dimensionCode", index);
    const dimensionValueCode = getRowField(row, "dimensionValueCode", index);

    if (!dimensionCode || !dimensionValueCode) {
      continue;
    }

    const matchedDimension = activeDimensions.find(
      (dimension) => dimension.code === dimensionCode
    );

    if (!matchedDimension) {
      continue;
    }

    dimensionValues[matchedDimension.key] = dimensionValueCode;
  }

  const dimensions: MpkDimensionSelection[] = activeDimensions
    .map((dimension) => {
      const dimensionValueCode = dimensionValues[dimension.key];

      if (!dimensionValueCode) {
        return null;
      }

      return {
        dimensionCode: dimension.code,
        dimensionValueCode,
      };
    })
    .filter(Boolean) as MpkDimensionSelection[];

  const dimension1 = activeDimensions.find((dimension) => dimension.order === 1);
  const dimension2 = activeDimensions.find((dimension) => dimension.order === 2);

  const dimension1ValueCode = dimension1
    ? dimensionValues[dimension1.key]
    : undefined;

  const dimension2ValueCode = dimension2
    ? dimensionValues[dimension2.key]
    : undefined;

  return {
    id: row.systemId || makeLocalId(),
    systemId: row.systemId,

    amount,
    percent,
    purchaseCost: row.purchaseCost,
    purchaseCostText: row.purchaseCostText,

    dimensionValues,
    dimensions,

    purchaserCode: dimension1ValueCode,
    purchaserName: findDimensionValueName(dimension1?.values, dimension1ValueCode),

    customerGroupCode: dimension2ValueCode,
    customerGroupName: findDimensionValueName(dimension2?.values, dimension2ValueCode),
  };
}

function buildDuplicateKey(
  row: MpkRow,
  activeDimensions: MPKDimensionDefinition[]
) {
  return activeDimensions
    .map((dimension) => row.dimensionValues[dimension.key] || "")
    .join("|");
}

export function useMpkDimensions(document: DocumentDTO, companyId: string) {
  const [rows, setRows] = useState<MpkRow[]>([]);
  const [dictionaries, setDictionaries] =
    useState<MPKDictionariesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const documentNetAmount = Number(document.amounts?.net || 0);

  useEffect(() => {
    if (!companyId || !document.id) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const dictionariesData = await getMPKDictionaries(companyId);
        const mpkRows = await getDocumentMPK(companyId, document.id);

        if (cancelled) return;

        const activeDimensions = dictionariesData.dimensions ?? [];

        setDictionaries(dictionariesData);
        setRows(
          mpkRows.map((row) =>
            normalizeExistingRow(row, activeDimensions, documentNetAmount)
          )
        );
      } catch (e) {
        console.error(e);

        if (!cancelled) {
          setError("Nie udało się pobrać MPK");
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [companyId, document.id, documentNetAmount]);

  const activeDimensions = useMemo(() => {
    return dictionaries?.dimensions ?? [];
  }, [dictionaries]);

  const hasAnyDimension =
    Boolean(dictionaries?.useMPKDimension) && activeDimensions.length > 0;

  /**
   * Tymczasowa kompatybilność ze starymi warunkami.
   */
  const hasDimension1 = activeDimensions.some(
    (dimension) => dimension.order === 1
  );

  const hasDimension2 = activeDimensions.some(
    (dimension) => dimension.order === 2
  );

  const dimensionHeaders = activeDimensions.map((dimension) => ({
    key: dimension.key,
    code: dimension.code,
    label: dimension.name || dimension.code,
    order: dimension.order,
    source: dimension.source,
  }));

  const totalAmount = useMemo(() => {
    return round2(
      rows.reduce((sum, row) => sum + Number(row.amount || 0), 0)
    );
  }, [rows]);

  const totalPercent = useMemo(() => {
    return round2(
      rows.reduce((sum, row) => sum + Number(row.percent || 0), 0)
    );
  }, [rows]);

  const remainingAmount = round2(documentNetAmount - totalAmount);

  const hasDuplicateRows = useMemo(() => {
    if (!hasAnyDimension || rows.length <= 1) {
      return false;
    }

    const keys = rows.map((row) =>
      buildDuplicateKey(row, activeDimensions)
    );

    return new Set(keys).size !== keys.length;
  }, [rows, activeDimensions, hasAnyDimension]);

  const isAmountValid = isAlmostEqual(totalAmount, documentNetAmount);
  const isPercentValid = isAlmostEqual(totalPercent, 100);

  const isMPKValid =
    !hasAnyDimension ||
    (
      rows.length > 0 &&
      isAmountValid &&
      isPercentValid &&
      !hasDuplicateRows
    );

  const editable = document.status?.code !== "Posted" && document.status?.code !== "Approved";

  return {
    rows,
    setRows,

    dictionaries,
    activeDimensions,

    loading,
    error,

    editable,

    hasAnyDimension,
    hasDimension1,
    hasDimension2,

    dimensionHeaders,

    totalAmount,
    totalPercent,
    remainingAmount,

    hasDuplicateRows,
    isAmountValid,
    isPercentValid,
    isMPKValid,
  };
}